import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate("/");
      }
    };
    checkSession();

    // Handle auth callback
    const handleAuthCallback = async () => {
      const error = searchParams.get("error");
      const errorDescription = searchParams.get("error_description");

      if (error) {
        toast({
          variant: "destructive",
          title: "Authentication Error",
          description: errorDescription || "Failed to authenticate. Please try again.",
        });
      }
    };

    if (window.location.hash || searchParams.has("error")) {
      handleAuthCallback();
    }
  }, [navigate, searchParams, toast]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (error) {
        trackEvent(ANALYTICS_EVENTS.SIGN_IN, { 
          success: false, 
          error: error.message 
        });
        
        let errorMessage = "Login failed. Please check your credentials.";
        
        if (error.message.includes("Invalid login credentials")) {
          errorMessage = "Invalid email or password. Please try again.";
        } else if (error.message.includes("Email not confirmed")) {
          errorMessage = "Please check your email and click the confirmation link.";
        } else if (error.message.includes("Too many requests")) {
          errorMessage = "Too many login attempts. Please try again later.";
        }
        
        toast({
          variant: "destructive",
          title: "Login failed",
          description: errorMessage,
        });
      } else if (data.user) {
        trackEvent(ANALYTICS_EVENTS.SIGN_IN, { 
          success: true,
          provider: 'email'
        });
        toast({
          title: "Success",
          description: "Welcome back!",
        });
        
        // Check if user has existing Posits and redirect accordingly
        const { data: posits, error: positsError } = await supabase
          .from('posits')
          .select('id')
          .eq('user_id', data.user.id)
          .limit(1);

        if (positsError) {
          // Fallback to home page if error
          setTimeout(() => navigate("/"), 100);
        } else if (posits && posits.length > 0) {
          // User has existing Posits, redirect to dashboard
          setTimeout(() => navigate("/dashboard"), 100);
        } else {
          // New user without Posits, redirect to home page
          setTimeout(() => navigate("/"), 100);
        }
      }
    } catch (error) {
      trackEvent(ANALYTICS_EVENTS.SIGN_IN, { 
        success: false, 
        error: 'Unexpected error'
      });
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setGoogleLoading(true);
      trackEvent(ANALYTICS_EVENTS.SIGN_IN, { 
        provider: 'google',
        status: 'initiated'
      });
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/login`,
        },
      });

      if (error) {
        trackEvent(ANALYTICS_EVENTS.SIGN_IN, { 
          success: false, 
          provider: 'google',
          error: error.message 
        });
        toast({
          variant: "destructive",
          title: "Login failed",
          description: error.message,
        });
      }
    } catch (error) {
      trackEvent(ANALYTICS_EVENTS.SIGN_IN, { 
        success: false,
        provider: 'google',
        error: 'Unexpected error'
      });
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast({
        variant: "destructive",
        title: "Email required",
        description: "Please enter your email address to reset your password.",
      });
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast({
        title: "Password reset email sent",
        description: "Check your email for the password reset link.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to send reset email.",
      });
    }
  };

  return (
    <div className="container max-w-md mx-auto px-4 py-12 animate-fade-in">
      <h1 className="text-4xl font-bold mb-2 tracking-tight">Welcome Back</h1>
      <p className="text-muted-foreground mb-8">Sign in to your account</p>
      <div className="space-y-6">
        <Button 
          variant="outline" 
          type="button" 
          className="w-full flex items-center justify-center gap-2 h-11 text-base font-medium shadow-sm hover:shadow-md transition-all duration-200" 
          onClick={handleGoogleLogin}
          disabled={googleLoading}
        >
          {googleLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
          )}
          Sign in with Google
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase tracking-wider font-medium">
            <span className="bg-background px-2 text-muted-foreground">
              Or continue with
            </span>
          </div>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="password">Password</Label>
            </div>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </div>
          <Button type="submit" className="w-full h-11 text-base font-medium shadow-sm hover:shadow-md transition-all duration-200 text-white" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Logging in...
              </>
            ) : (
              "Log in"
            )}
          </Button>
          <div className="text-center">
            <Button
              type="button"
              variant="link"
              className="px-0 font-normal mt-2"
              onClick={handleForgotPassword}
            >
              Forgot password?
            </Button>
          </div>
        </form>
        <p className="text-center text-sm text-muted-foreground">
          Don't have an account?{" "}
          <Link to="/signup" className="text-primary hover:underline">
            Get started here
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;